import {
  DEFAULT_PHONE_COUNTRY_CODE,
  formatPhoneNumberToDisplay,
  sanitizePhoneNumber,
} from '@helpers/utils';
import { ErrorDetailsDtoErrorCodeEnum, UserTableDto, Role } from '@interfaces/models';
import { AssignmentRoles, getRoleColor } from '@shared/utils';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useUsers } from '@pages/users/hooks/use-users';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetCostCenters } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { Badge, toast } from '@ui/components';
import { useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { TFunction } from 'i18next';
import { z } from 'zod';

const USER_FORM_DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  phoneExt: DEFAULT_PHONE_COUNTRY_CODE,
  phoneNumber: '',
  role: '',
  costCenters: [] as string[],
};

const formDefaultValue = (user: UserTableDto) => {
  if (!user) return USER_FORM_DEFAULT_VALUES;
  return {
    firstName: user.firstName ?? USER_FORM_DEFAULT_VALUES.firstName,
    lastName: user.lastName ?? USER_FORM_DEFAULT_VALUES.lastName,
    email: user.email ?? USER_FORM_DEFAULT_VALUES.email,
    phoneExt: user.phoneExt ?? USER_FORM_DEFAULT_VALUES.phoneExt,
    phoneNumber: formatPhoneNumberToDisplay(
      user.phoneNumber ?? USER_FORM_DEFAULT_VALUES.phoneNumber,
    ),
    role: user.role ?? USER_FORM_DEFAULT_VALUES.role,
    costCenters:
      user.costCenters?.map((costCenter) => costCenter.id) ?? USER_FORM_DEFAULT_VALUES.costCenters,
  };
};

const formSchema = (t: TFunction) =>
  z.object({
    firstName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    lastName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 })),
    email: z.string({ required_error: t('FORMS.REQUIRED') }).email(t('FORMS.INVALID_EMAIL')),
    phoneExt: z.string().optional(),
    phoneNumber: z
      .string()
      .min(9, t('FORMS.MIN_CHARACTERS', { limit: 7 })) // min 9 because validator count also white spaces from mask
      .max(19, t('FORMS.MAX_CHARACTERS', { limit: 15 })) // 15 is max, we have 4 spaces in mask
      .optional()
      .or(z.literal('')),
    role: z.string({ required_error: t('FORMS.REQUIRED') }).nonempty(t('FORMS.REQUIRED')),
    costCenters: z.array(z.string()).min(1, t('FORMS.REQUIRED')),
  });

export const useUserEdit = (user: UserTableDto, onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const {
    costCentersOptions,
    isFetching: isCostCentersFetching,
    defaultCostCenter,
  } = useGetCostCenters();

  const roleOptions =
    AssignmentRoles?.map((role) => ({
      label: (
        <Badge variant={getRoleColor(role)} size="large">
          {t(`AUTH.ROLE.${role}`)}
        </Badge>
      ),
      value: role,
    })) || [];

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: formDefaultValue(user),
  });

  const { editUser, isEditUserPending } = useUsers({
    onEditUserSuccess: () => {
      toast.success(t('USERS.EDIT_USER_SIDEBAR_SUCCESS'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess();
    },
    onEditUserError: ({ errorCode }) => {
      if (errorCode === ErrorDetailsDtoErrorCodeEnum.USER_EDIT_PHONE_NUMBER_NOT_UNIQUE) {
        return form.setError('phoneNumber', {
          message: t(`API_ERRORS.${errorCode}`),
          type: 'custom',
        });
      }

      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  const selectedCostCenters = form.watch('costCenters');

  useDeepCompareEffect(() => {
    if (!selectedCostCenters.length && defaultCostCenter) {
      form.setValue('costCenters', [defaultCostCenter.id]);
    }
  }, [defaultCostCenter, selectedCostCenters]);

  const onSubmit = useCallback(() => {
    const value = form.getValues();
    // TODO: remove marketingConsents once the backend is updated
    editUser({
      data: {
        ...value,
        phoneNumber: sanitizePhoneNumber(value.phoneNumber || ''),
        role: value.role as Role,
      },
      id: user.id,
    });
  }, [form, editUser, user]);

  return {
    isCostCentersFetching,
    costCentersOptions,
    isEditUserPending,
    roleOptions,
    onSubmit,
    form,
    t,
  };
};
